import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Image as ImageIcon, LogOut, FolderTree } from 'lucide-react';
import { clientApi } from '../api';
import BrandLogo from '../components/BrandLogo';
import { toast } from 'sonner';

const ClientGallery = () => {
  const { clientSlug, gallerySlug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null); // { gallery, subgalleries }
  const displayName = clientApi.displayName(clientSlug);

  useEffect(() => {
    if (!clientApi.hasToken(clientSlug)) { nav(`/c/${clientSlug}`, { replace: true }); return; }
    (async () => {
      try { const d = await clientApi.getGallery(clientSlug, gallerySlug); setData(d); }
      catch (err) { toast.error('Galleria non trovata'); nav(`/c/${clientSlug}/home`); }
    })();
  }, [clientSlug, gallerySlug, nav]);

  const logout = () => { clientApi.logout(clientSlug); nav(`/c/${clientSlug}`); };
  if (!data) return null;
  const { gallery, subgalleries } = data;

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-4">
          <Link to={`/c/${clientSlug}/home`} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white"><ArrowLeft className="w-4 h-4" /> Lavori</Link>
          <div className="h-4 w-px bg-white/10" />
          <BrandLogo size="sm" hideText />
          <div className="min-w-0 ml-2">
            <p className="text-[10px] uppercase tracking-widest text-[#C9A961]">{gallery.category} · {gallery.date}</p>
            <h1 className="font-display text-lg md:text-xl truncate">{gallery.title}</h1>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <span className="text-xs text-slate-400 hidden sm:inline">Benvenuto/a, <b className="text-white">{displayName}</b></span>
            <button onClick={logout} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1.5"><LogOut className="w-3.5 h-3.5" /> Esci</button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#C9A961]/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-[#C9A961]/30 text-[#C9A961] text-[11px] uppercase tracking-[0.25em] mb-4">
            <FolderTree className="w-3.5 h-3.5" /> Sotto-gallerie
          </div>
          <h1 className="font-display text-4xl md:text-6xl italic mb-3">{gallery.title}</h1>
          <p className="text-slate-400">Scegli una cartella per vedere, commentare e scaricare le foto.</p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 md:px-6 pb-20">
        {subgalleries.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
            <ImageIcon className="w-10 h-10 text-[#C9A961] mx-auto mb-3" />
            <p className="text-slate-300">Nicola sta preparando le foto. Torna presto!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subgalleries.map((s, idx) => (
              <Link key={s.id} to={`/c/${clientSlug}/lavori/${gallerySlug}/${s.slug}`} className="group relative rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-[#C9A961]/50 transition-all duration-500">
                <div className="aspect-[4/5] overflow-hidden bg-black">
                  {s.cover_url ? <img src={s.cover_url} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : <div className="w-full h-full grid place-items-center text-slate-600"><ImageIcon /></div>}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">Sotto {idx + 1}</div>
                <div className="absolute bottom-0 inset-x-0 p-5">
                  <div className="flex items-center gap-2 text-[11px] text-slate-300 mb-2">
                    <span className="inline-flex items-center gap-1 bg-white/10 border border-white/10 rounded-full px-2 py-0.5"><ImageIcon className="w-3 h-3" /> {s.photo_count} foto</span>
                  </div>
                  <h3 className="font-display text-2xl text-white">{s.title}</h3>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#C9A961] font-semibold">
                    Apri <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ClientGallery;
